/**
 * Stage 5E.2 Phase 3 — shared brand payload normaliser.
 *
 * Accepts EITHER the canonical Stage 5E.2 §8.1 envelope OR the
 * Stage 5D legacy flat shape (mode='firm_branded' + brand.*), and
 * returns one normalised object that the shared BrandProvider /
 * components consume.
 *
 * Contract §8.2:
 *   wire branding_mode='firm_first'    → normalised mode='firm_first'
 *   wire branding_mode='standard_ordly'→ normalised mode='standard_ordly'
 *   legacy Stage 5D mode='firm_branded' (no branding_mode) → 'firm_first'
 *   legacy Stage 5D mode='ordly'       → 'standard_ordly'
 *
 * The normaliser does NOT re-derive token values (contract §13.2:
 * the backend is the source of truth). When a legacy payload lacks
 * the tokens / logos blocks, the normaliser fills them from the
 * legacy brand.* flat keys + Ordly secondary fallbacks (matching
 * the backend co_brand→firm_first envelope mapping).
 */

// Ordly subtle secondary fallback for legacy rows (mirrors backend).
const ORDLY_SECONDARY_SURFACE = "#eff3ed";
const ORDLY_SECONDARY_BORDER = "#cdd8c9";

export function normalizeBrandPayload(body) {
    if (!body || typeof body !== "object") {
        return ordlyNormalised();
    }

    // Resolve canonical wire mode (Stage 5E.2 §8.1 / §9).
    // Accepts BOTH the canonical Phase 2 envelope (mode='firm_first')
    // AND the Stage 5D legacy shape (mode='firm_branded').
    const wireMode =
        body.branding_mode ||
        (body.mode === "firm_first" || body.mode === "firm_branded"
            ? "firm_first"
            : "standard_ordly");

    if (wireMode === "standard_ordly") {
        return ordlyNormalised();
    }

    // Firm path. Build display + contact block.
    const legacy = body.brand || null; // Stage 5D mirror
    const displayName =
        body.display_name || legacy?.display_name || null;
    const contactEmail =
        body.contact_email || legacy?.contact_email || null;
    const contactPhone = body.contact_phone || null;
    const website = body.website || null;

    // Logos: prefer the canonical block, otherwise build from the
    // legacy flat logo_url (no compact/light/dark variants in Stage 5D).
    const logos = body.logos || {
        primary: legacy?.logo_url || null,
        compact: null,
        light: null,
        dark: null,
    };

    // Tokens: prefer the canonical block; otherwise derive what we
    // can from the legacy accent fields with Ordly secondary fallbacks.
    const tokens = body.tokens || {
        primary: legacy?.accent_hex || null,
        on_primary: legacy?.on_accent_hex || null,
        primary_hover: legacy?.accent_hover_hex || null,
        primary_pressed: null,
        focus_ring: legacy?.accent_focus_ring_hex || null,
        secondary: ORDLY_SECONDARY_BORDER,
        secondary_surface: ORDLY_SECONDARY_SURFACE,
        secondary_border: ORDLY_SECONDARY_BORDER,
        surface_tint: ORDLY_SECONDARY_SURFACE,
    };

    // Attribution: canonical block wins; fall back to the legacy
    // platform.powered_by_ordly boolean.
    const attribution =
        body.attribution ||
        {
            show_powered_by_ordly: !!body.platform?.powered_by_ordly,
        };

    return {
        mode: "firm_first",
        displayName,
        contactEmail,
        contactPhone,
        website,
        logos,
        tokens,
        attribution,
    };
}

export function ordlyNormalised() {
    return {
        mode: "standard_ordly",
        displayName: null,
        contactEmail: null,
        contactPhone: null,
        website: null,
        logos: null,
        tokens: null,
        attribution: { show_powered_by_ordly: false },
    };
}
